<template>
  <div class="mb-3">
    <div class="row mx-0 py-2 shadow" :class="bgColorClass">
      <div class="col-4 d-flex my-auto">
        {{ archivUser.benutzername }}
      </div>

      <div class="col-3 my-auto">{{ archivUser.email }}</div>
    </div>

    <div v-if="user == undefined && conflictUser != undefined">
      <small class="text-danger ms-3">Die E-Mail-Adresse ist bereits vergeben.</small>
    </div>

    <div v-if="user == undefined && (archivUser.email == undefined || archivUser.email.trim() == '')">
      <small class="text-danger ms-3">Der Benutzer hat keine E-Mail-Adresse und kann nicht importiert werden.</small>
    </div>

    <div v-if="user == undefined && conflictUser == undefined && mergeUser != undefined" class="row mx-0 bg-white shadow rounded-bottom">
      <div class="col-4 d-flex py-2 my-auto">{{ mergeUser.firstName }} {{ mergeUser.lastName }}</div>

      <div class="col-3 py-2 my-auto">{{ mergeUser.email }}</div>

      <div class="col-2 offset-3 d-flex justify-content-end p-0">
        <button type="button" class="btn btn-primary btn-sm w-100 rounded-0" @click="mergeArchivUser()">
          <div v-if="loading && thisLoading" class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>

          <div v-else>
            <font-awesome-icon icon="code-merge" class="me-2" />
            Merge
          </div>
        </button>
      </div>
    </div>

    <div
      v-if="user == undefined && mergeUser == undefined && archivUser.email != undefined && archivUser.email.trim() != ''"
      class="row mx-0 shadow rounded-bottom"
    >
      <div class="col-3 py-1 my-auto">
        <input class="form-control form-control-sm" type="text" placeholder="Vorname" aria-label="Vorname" v-model="newUser.firstName" />
      </div>

      <div class="col-3 py-1 my-auto">
        <input class="form-control form-control-sm" type="text" placeholder="Nachname" aria-label="Nachname" v-model="newUser.lastName" />
      </div>

      <div class="col-3 py-1 my-auto">
        <input class="form-control form-control-sm" type="text" placeholder="E-Mail" aria-label="E-Mail" v-model="newUser.email" />
      </div>

      <div class="col-2 offset-1 d-flex justify-content-end p-0">
        <button type="button" class="btn btn-success btn-sm w-100 rounded-0" @click="createArchivUser()">
          <div v-if="loading && thisLoading" class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>

          <div v-else>
            <font-awesome-icon icon="file-import" class="me-2" />
            Import
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { PropType, computed, onMounted, ref } from 'vue';
  import { useUnternehmenStore } from '@/stores/unternehmenStore';
  import { useCommonStore } from '@/stores/commonStore';

  import { ArchivUser, MergeUserRequest, User, ImportUserRequest } from '@/models/BenutzerModels';
  import { Unternehmen } from '@/models/UnternehmenModels';
  //#endregion

  const { archivUser, user, mergeUser, conflictUser, unternehmen } = defineProps({
    archivUser: {
      type: Object as PropType<ArchivUser>,
      required: true,
    },
    user: {
      type: Object as PropType<User>,
      required: false,
      default: undefined,
    },
    mergeUser: {
      type: Object as PropType<User>,
      required: false,
      default: undefined,
    },
    conflictUser: {
      type: Object as PropType<User>,
      required: false,
      default: undefined,
    },
    unternehmen: {
      type: Object as PropType<Unternehmen>,
      required: true,
    },
    // realm: {
    //   type: String as PropType<string>,
    //   default: 'admin',
    // },
  });

  const unternehmenStore = useUnternehmenStore();
  const commonStore = useCommonStore();

  const thisLoading = ref(false);
  const loading = computed(() => commonStore.status.updateArchivUserLoading);
  const errorMsg = computed(() => commonStore.status.archivUserErrorMsg);

  const bgColorClass = computed(() => {
    if (user != undefined) {
      return 'bg-success bg-opacity-25 rounded';
    } else if (conflictUser != undefined || archivUser.email == undefined || archivUser.email.trim() == '') {
      return 'bg-white border border-danger rounded';
    } else if (conflictUser != undefined && mergeUser != undefined) {
      return 'bg-white border border-danger rounded';
    } else if (mergeUser != undefined) {
      return 'bg-warning bg-opacity-25 rounded-top';
    }

    return 'bg-white rounded-top';
  });

  async function mergeArchivUser() {
    thisLoading.value = true;

    if (mergeUser != undefined && conflictUser == undefined) {
      archivUser.guid = mergeUser.id;
      const request = new MergeUserRequest(unternehmen.id, archivUser);

      await unternehmenStore.mergeArchivUser(request);

      if (errorMsg.value == '') {
        await Promise.all([unternehmenStore.getUnternehmenUsers(unternehmen.realm), unternehmenStore.getUnternehmenArchivUsers(unternehmen.id)]);
      }
    }

    thisLoading.value = false;
  }

  const newUser = ref<User>(new User());

  async function createArchivUser() {
    thisLoading.value = true;

    newUser.value.enabled = true;
    newUser.value.emailVerified = true;

    const request = new ImportUserRequest(unternehmen.id, archivUser.id, newUser.value);
    await unternehmenStore.importArchivUser(request);

    if (errorMsg.value == '') {
      await Promise.all([unternehmenStore.getUnternehmenUsers(unternehmen.realm), unternehmenStore.getUnternehmenArchivUsers(unternehmen.id)]);
    }

    thisLoading.value = false;
  }

  onMounted(() => {
    const nameArray = archivUser.benutzername.split(' ');

    if (nameArray.length > 0) {
      newUser.value.firstName = nameArray[0];
    }
    if (nameArray.length > 1) {
      newUser.value.lastName = nameArray.slice(1).join(' ');
    }

    newUser.value.email = archivUser.email;
    newUser.value.username = archivUser.email;
  });
</script>

<style lang="scss" scoped>
  .benutzer-row:hover {
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
      background-color: #cee0fa !important;
    }
  }
</style>
